import type theme from '#build/ui/select'

// Recursively make all properties optional
type DeepPartial<T> = {
	[P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P]
}

export default {
	slots: {
		base: [
			'relative group rounded-xs inline-flex items-center focus:outline-none disabled:cursor-not-allowed disabled:opacity-75',
			'transition-colors'
		],
		leading: 'absolute inset-y-0 start-0 flex items-center',
		leadingIcon: 'shrink-0 text-dimmed',
		leadingAvatar: 'shrink-0',
		leadingAvatarSize: '',
		trailing: 'absolute inset-y-0 end-0 flex items-center',
		trailingIcon: 'shrink-0 text-dimmed',
		value: 'truncate pointer-events-none',
		placeholder: 'truncate text-dimmed',
		arrow: 'fill-default',
		content: 'max-h-60 w-(--reka-select-trigger-width) bg-default shadow-lg rounded-md ring ring-default overflow-hidden data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] origin-(--reka-select-content-transform-origin) pointer-events-auto',
		viewport: 'divide-y divide-default scroll-py-1',
		group: 'p-1 isolate',
		empty: 'py-2 text-center text-sm text-muted',
		label: 'font-semibold text-highlighted',
		separator: '-mx-1 my-1 h-px bg-border',
		item: [
			'group relative w-full flex items-center select-none outline-none before:absolute before:z-[-1] before:inset-px before:rounded-md data-disabled:cursor-not-allowed data-disabled:opacity-75 text-default data-highlighted:not-data-disabled:text-highlighted data-highlighted:not-data-disabled:before:bg-accented',
			'transition-colors before:transition-colors'
		],
		itemLeadingIcon: [
			'shrink-0 text-dimmed group-data-highlighted:not-group-data-disabled:text-default',
			'transition-colors'
		],
		itemLeadingAvatar: 'shrink-0',
		itemLeadingAvatarSize: '',
		itemLeadingChip: 'shrink-0',
		itemLeadingChipSize: '',
		itemTrailing: 'ms-auto inline-flex gap-1.5 items-center',
		itemTrailingIcon: 'shrink-0 text-primary',
		itemLabel: 'truncate'
	},
	variants: {
		buttonGroup: {
			horizontal: 'not-only:first:rounded-e-none not-only:last:rounded-s-none not-last:not-first:rounded-none',
			vertical: 'not-only:first:rounded-b-none not-only:last:rounded-t-none not-last:not-first:rounded-none'
		},
		size: {
			xs: {
				base: 'px-2 py-1 text-xs gap-1',
				leading: 'ps-2',
				trailing: 'pe-2',
				leadingIcon: 'size-4',
				leadingAvatarSize: '3xs',
				trailingIcon: 'size-4',
				label: 'p-1 text-[10px]/3 gap-1',
				item: 'p-1 text-xs gap-1',
				itemLeadingIcon: 'size-4',
				itemLeadingAvatarSize: '3xs',
				itemLeadingChip: 'size-4',
				itemLeadingChipSize: 'sm',
				itemTrailingIcon: 'size-4'
			},
			sm: {
				base: 'px-2.5 py-1.5 text-xs gap-1.5',
				leading: 'ps-2.5',
				trailing: 'pe-2.5',
				leadingIcon: 'size-4',
				leadingAvatarSize: '3xs',
				trailingIcon: 'size-4',
				label: 'p-1.5 text-[10px]/3 gap-1.5',
				item: 'p-1.5 text-xs gap-1.5',
				itemLeadingIcon: 'size-4',
				itemLeadingAvatarSize: '3xs',
				itemLeadingChip: 'size-4',
				itemLeadingChipSize: 'sm',
				itemTrailingIcon: 'size-4'
			},
			md: {
				base: 'px-2.5 py-1.5 text-sm gap-1.5',
				leading: 'ps-2.5',
				trailing: 'pe-2.5',
				leadingIcon: 'size-5',
				leadingAvatarSize: '2xs',
				trailingIcon: 'size-5',
				label: 'p-1.5 text-xs gap-1.5',
				item: 'p-1.5 text-sm gap-1.5',
				itemLeadingIcon: 'size-5',
				itemLeadingAvatarSize: '2xs',
				itemLeadingChip: 'size-5',
				itemLeadingChipSize: 'md',
				itemTrailingIcon: 'size-5'
			},
			lg: {
				base: 'px-3 py-2 text-sm gap-2',
				leading: 'ps-3',
				trailing: 'pe-3',
				leadingIcon: 'size-5',
				leadingAvatarSize: '2xs',
				trailingIcon: 'size-5',
				label: 'p-2 text-xs gap-2',
				item: 'p-2 text-sm gap-2',
				itemLeadingIcon: 'size-5',
				itemLeadingAvatarSize: '2xs',
				itemLeadingChip: 'size-5',
				itemLeadingChipSize: 'md',
				itemTrailingIcon: 'size-5'
			},
			xl: {
				base: 'px-4 py-3 text-base gap-2.5',
				leading: 'ps-3.5',
				trailing: 'pe-3.5',
				leadingIcon: 'size-7',
				leadingAvatarSize: 'lg',
				trailingIcon: 'size-7',
				label: 'p-2.5 text-sm gap-2.5',
				item: 'p-2.5 text-base gap-2.5',
				itemLeadingIcon: 'size-7',
				itemLeadingAvatarSize: 'lg',
				itemLeadingChip: 'size-7',
				itemLeadingChipSize: 'lg',
				itemTrailingIcon: 'size-7'
			}
		},
		variant: {
			outline: 'text-highlighted bg-default ring ring-inset ring-dimmed',
			soft: 'text-highlighted bg-elevated/50 hover:bg-elevated focus:bg-elevated disabled:bg-elevated/50',
			subtle: 'text-highlighted bg-elevated ring ring-inset ring-accented',
			ghost: 'text-highlighted bg-transparent hover:bg-elevated focus:bg-elevated disabled:bg-transparent dark:disabled:bg-transparent',
			none: 'text-highlighted bg-transparent'
		},
		color: {
			primary: '',
			secondary: '',
			success: '',
			info: '',
			warning: '',
			error: '',
			neutral: ''
		},
		leading: {
			true: ''
		},
		trailing: {
			true: ''
		},
		loading: {
			true: ''
		},
		highlight: {
			true: ''
		},
		type: {
			file: 'file:me-1.5 file:font-medium file:text-muted file:outline-none'
		}
	},
	compoundVariants: [
		{
			color: 'primary',
			variant: [
				'outline',
				'subtle'
			],
			class: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accented/75'
		},
		{
			color: 'primary',
			highlight: true,
			class: 'ring ring-inset ring-accented'
		},
		{
			color: 'neutral',
			variant: [
				'outline',
				'subtle'
			],
			class: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inverted'
		},
		{
			color: 'neutral',
			highlight: true,
			class: 'ring ring-inset ring-inverted'
		},
		{
			leading: true,
			size: 'xs',
			class: 'ps-7'
		},
		{
			leading: true,
			size: 'sm',
			class: 'ps-8'
		},
		{
			leading: true,
			size: 'md',
			class: 'ps-9'
		},
		{
			leading: true,
			size: 'lg',
			class: 'ps-10'
		},
		{
			leading: true,
			size: 'xl',
			class: 'ps-11'
		},
		{
			trailing: true,
			size: 'xs',
			class: 'pe-7'
		},
		{
			trailing: true,
			size: 'sm',
			class: 'pe-8'
		},
		{
			trailing: true,
			size: 'md',
			class: 'pe-9'
		},
		{
			trailing: true,
			size: 'lg',
			class: 'pe-10'
		},
		{
			trailing: true,
			size: 'xl',
			class: 'pe-11'
		},
		{
			loading: true,
			leading: true,
			class: {
				leadingIcon: 'animate-spin'
			}
		},
		{
			loading: true,
			leading: false,
			trailing: true,
			class: {
				trailingIcon: 'animate-spin'
			}
		}
	],
	defaultVariants: {
		size: 'xl',
		color: 'primary',
		variant: 'outline'
	}
} satisfies DeepPartial<typeof theme>
